import React, { useEffect } from 'react';

import { startsWith, isObject } from 'lodash';

import { useLocation } from 'react-router-dom';

import getJwt from 'core/axios/getJwt';
import useLocalStorage from 'hooks/useLocalStorage';
import useRedirect from 'hooks/useRedirect';
import useUserRedux from 'hooks/useUserRedux';
import { LOGIN, NEWSFEED } from 'routes/constants';

import CounselorRedirects from 'routes/CounselorRoutes/CounselorRedirects';
import MemberRedirects from 'routes/MemberRoutes/MemberRedirects';
import StudentRedirect from 'routes/StudentRoutes/StudentRedirect';

import { ROUTES_WITHOUT_REDIRECTION } from 'modules/LoginRedirect/constants';

/**
 * Contains all general redirect logic for loginRedirectURL and user type specific redirects.
 */
const LoginRedirect = () => {
  const [loginRedirectURL, setLoginRedirectURL] = useLocalStorage(
    'loginRedirectURL',
    '',
  );
  const { redirect } = useRedirect();

  const { isStudent, isCounselor, isMember, isOAuth2Authenticated } =
    useUserRedux();

  const { pathname, search } = useLocation();
  const jwt = getJwt();
  const isRedirectedFromOnboarding = sessionStorage.getItem(
    'isRedirectedFromOnboarding',
  );

  const isLoggedIn = Boolean(isOAuth2Authenticated || jwt);

  useEffect(() => {
    const shouldNotBeRedirected = Boolean(
      ROUTES_WITHOUT_REDIRECTION.find(route => startsWith(pathname, route)) ||
        pathname === '/',
    );
    /*
      Check these to redirect users to the links that they provided while they're logged out:
        - IF user is not logged in
        - IF user already doesn't have a redirect URL provided by other modules
        - Do not consider login path @('/login') as a path should be saved
    */
    const shouldSaveRoute =
      !shouldNotBeRedirected && !isLoggedIn && !loginRedirectURL;
    if (shouldSaveRoute) {
      setLoginRedirectURL({
        // Fix symptom where sometimes path name here is receiving "//"
        to: `${pathname}${search}`.replace(/\/+/g, '/'),
        reason:
          'Redirect user to the path that user has requested to visit before logging in.',
        component: 'LoginRedirect',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, loginRedirectURL, pathname, search]);

  useEffect(() => {
    // This effect handles redirection for the onboarding flow after a member creates an account and logs in.
    // If isRedirectedFromOnboarding is true and the user is on the /newsfeed page, redirect them to the URL stored in loginRedirectURL.
    if (
      isObject(loginRedirectURL) &&
      isRedirectedFromOnboarding &&
      pathname === NEWSFEED
    ) {
      const { reason, ...rest } = loginRedirectURL;
      redirect({
        reason: `${reason}. Redirected by loginRedirectURL (localStorage) in modules/LoginRedirect. isRedirectedFromOnboarding was set to True.`,
        ...rest,
      });
      setLoginRedirectURL('');
      sessionStorage.removeItem('isRedirectedFromOnboarding');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isRedirectedFromOnboarding,
    loginRedirectURL,
    pathname,
    setLoginRedirectURL,
  ]);

  useEffect(() => {
    // It's possible for a user to be considered logged in while still on the home page.
    // When this occurs, loginRedirectURL may be hit prematurely causing redirect issues.
    const notLoginRoute = !pathname.includes(LOGIN);

    // loginRedirectURL is stored in localStorage
    if (
      loginRedirectURL &&
      isLoggedIn &&
      notLoginRoute &&
      !isRedirectedFromOnboarding
    ) {
      // It's more preferable to set loginRedirectURL as an object for
      // useRedirect as it provides more meta information about the navigation
      // Refer to hooks/useRedirect/README for more info
      if (isObject(loginRedirectURL)) {
        const { reason, ...rest } = loginRedirectURL;
        redirect({
          reason: `${reason}. Redirected by loginRedirectURL (localStorage) in modules/LoginRedirect`,
          ...rest,
        });
        setLoginRedirectURL('');
        return;
      }

      // loginRedirectURL can also be a string path but does not provide as much information
      redirect({
        to: loginRedirectURL,
        component: 'modules/LoginRedirect/index.js / LoginRedirect',
        reason: `loginRedirectURL (stored in localStorage) was populated with ${loginRedirectURL}`,
        options: { replace: true },
      });
      setLoginRedirectURL('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginRedirectURL, isLoggedIn, pathname, isRedirectedFromOnboarding]);

  return (
    <>
      {isCounselor && <CounselorRedirects />}
      {isMember && <MemberRedirects />}

      {/* Alumni users can be both students and members. This conditional is required so */}
      {/* alumni users don't have access to any of these student redirection routes */}
      {isStudent && !isMember && <StudentRedirect />}
    </>
  );
};

export default LoginRedirect;
